import { useMemo, useCallback } from "react";
import { InformationSource } from "@/types/information-source/information-source.types";
import { FilterOption } from "@/components/ui/table-header/types";
import { normalizeText } from "@/utils/textFormatter";

interface UseInformationSourceFiltersProps {
  infoSources: InformationSource[];
  searchTerm: string;
  showArchived: boolean;
  nameFilters: string[];
  yearFilters: string[];
}

export const useInformationSourceFilters = ({
  infoSources,
  searchTerm,
  showArchived,
  nameFilters,
  yearFilters,
}: UseInformationSourceFiltersProps) => {
  const filteredSources = useMemo(() => {
    if (!infoSources) return [];

    return infoSources.filter((source) => {
      // Filter by archive status - show either archived or non-archived sources
      if (showArchived) {
        if (!source.isArchived) return false;
      } else {
        if (source.isArchived) return false;
      }

      // Filter by name
      if (nameFilters.length > 0 && !nameFilters.includes(source.name)) {
        return false;
      }

      // Filter by year
      if (yearFilters.length > 0) {
        const sourceYear = source.year?.toString();
        if (!sourceYear || !yearFilters.includes(sourceYear)) {
          return false;
        }
      }

      // Filter by search term
      if (searchTerm) {
        const term = normalizeText(searchTerm);
        const normalizedName = normalizeText(source.name);
        const normalizedLink = source.link ? normalizeText(source.link) : "";
        const normalizedYear = source.year?.toString() || "";

        return normalizedName.includes(term) || normalizedLink.includes(term) || normalizedYear.includes(term);
      }

      return true;
    });
  }, [infoSources, searchTerm, showArchived, nameFilters, yearFilters, normalizeText]);

  const getUniqueOptions = useCallback(
    (field: keyof InformationSource | "year"): FilterOption[] => {
      if (!infoSources) return [];

      // Filter sources by all criteria except the current field being queried
      const filteredByOtherFilters = infoSources.filter((source) => {
        // Filter by archive status
        if (showArchived) {
          if (!source.isArchived) return false;
        } else {
          if (source.isArchived) return false;
        }

        // Filter by search term
        if (searchTerm) {
          const term = normalizeText(searchTerm);
          const normalizedName = normalizeText(source.name);
          const normalizedLink = source.link ? normalizeText(source.link) : "";
          const normalizedYear = source.year?.toString() || "";

          if (!normalizedName.includes(term) && !normalizedLink.includes(term) && !normalizedYear.includes(term)) {
            return false;
          }
        }

        // When querying for a specific field's options, we don't filter by that field
        // This allows us to show all possible options for that field based on other selected filters

        // Filter by name (if not the current field)
        if (field !== "name" && nameFilters.length > 0 && !nameFilters.includes(source.name)) {
          return false;
        }

        // Filter by year (if not the current field)
        if (field !== "year" && yearFilters.length > 0) {
          const sourceYear = source.year?.toString();
          if (!sourceYear || !yearFilters.includes(sourceYear)) {
            return false;
          }
        }

        return true;
      });

      // Extract unique values for the field from the filtered sources
      const uniqueValues = new Set<string>();

      filteredByOtherFilters.forEach((source) => {
        if (field === "year") {
          if (source.year) {
            uniqueValues.add(source.year.toString());
          }
        } else {
          const value = source[field];
          if (value && typeof value === "string") {
            uniqueValues.add(value);
          }
        }
      });

      return Array.from(uniqueValues)
        .sort((a, b) => {
          // For year field, sort numerically from most recent to oldest
          if (field === "year") {
            return parseInt(b) - parseInt(a);
          }
          // For other fields, sort alphabetically
          return a.localeCompare(b);
        })
        .map((value) => ({
          label: value,
          value,
        }));
    },
    [infoSources, searchTerm, showArchived, nameFilters, yearFilters, normalizeText]
  );

  return {
    filteredSources,
    getUniqueOptions,
  };
};
